import { useState, useEffect } from "react";

import { BookingSearchApi } from "../../Api/BookingSearchApi";

import { BookingSearchResponse } from "../../../../Api/BookingSearch/Model/BookingSearchResponse.csharp";
import { LocationDto } from "@apiModels/LocationDto.csharp";

import { useDebouncedCallback } from "use-debounce";

export const useBookingSearch = (
  location: LocationDto,
  searchValue: string
) => {
  const [results, setResults] = useState<BookingSearchResponse>(
    {} as BookingSearchResponse
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showAllDoctors, setShowAllDoctors] = useState(false);
  const [showAllServices, setShowAllServices] = useState(false);
  const [showAllSpecializations, setShowAllSpecializations] = useState(false);
  const [isTyping, setIsTyping] = useState(false);

  const performSearch = async (
    keyword: string,
    showAllDoctors = false,
    showAllServices = false,
    showAllSpecializations = false
  ) => {
    const trimmed = keyword.trim();
    if (trimmed.length < 2) {
      setResults({} as BookingSearchResponse);
      return;
    }
    setIsTyping(false);
    setIsLoading(true);
    setError(null);

    try {
      let centerIds: string[] = [];

      if (location.isLocation && location.id !== "-1") {
        centerIds = location.centers ?? [];
      } else if (!location.isLocation && location.id !== "-1") {
        centerIds = [location.id];
      }

      const totalServices = results.services?.totalCount ?? 3;
      const totalDoctors = results.doctors?.totalCount ?? 3;
      const totalSpecializations = results.specializations?.totalCount ?? 3;

      const bookingSearchApi = new BookingSearchApi();

      const data = await bookingSearchApi.search(
        trimmed,
        centerIds,
        showAllDoctors ? totalDoctors : undefined,
        showAllServices ? totalServices : undefined,
        showAllSpecializations ? totalSpecializations : undefined
      );

      setResults(data);
    } catch (err: any) {
      setError(err.message || "Error fetching search results");
      setResults({} as BookingSearchResponse);
    } finally {
      setIsLoading(false);
    }
  };

  const searchDebounced = useDebouncedCallback(
    (keyword: string) => performSearch(keyword, false),
    500
  );

  useEffect(() => {
    setIsTyping(true);
    setShowAllDoctors(false);
    setShowAllServices(false);
    setShowAllSpecializations(false);
    searchDebounced(searchValue);
  }, [searchValue]);

  return {
    searchValue,
    results,
    setResults,
    isLoading,
    error,
    isTyping,
    showAllDoctors,
    setShowAllDoctors,
    showAllServices,
    setShowAllServices,
    showAllSpecializations,
    setShowAllSpecializations,
    performSearch,
  };
};
